import {
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import CalendarSchedule from 'src/components/calendar/calendar-schedule';
import CalendarScheduleSkeleton from 'src/components/calendar/calendar-schedule-skeleton';
import Iconify from 'src/components/iconify';
import { translateKey } from 'src/locales/translate';
import ScheduleConflictWarningModal from 'src/sections/@dashboard/organization/components/ScheduleConflictWarningModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import TimeOffModal from 'src/sections/@dashboard/visit/components/TimeOffModal';
import { AvailabilitySlotDto, ConflictingVisitDto } from 'woofwoof-api';


export default function OrganizationDetailsOrganizationScheduleTabsPage() {
  const theme = useTheme();
  const { organizationId } = useParams();
  const { isOrganizationAdminRoleSelected } = useOrganizationContext();
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictingVisits, setConflictingVisits] = useState<ConflictingVisitDto[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  // Holds the pending apply + the deferred promise handles while the confirm
  // modal is open (set when a dry-run preview surfaces conflicts).
  const pendingChangeRef = useRef<{
    apply: () => Promise<AxiosResponse<any>>;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  // Organization schedule data (for organization admins)
  const {
    data: organizationData,
    refetch: refetchOrganization,
    isLoading: isOrganizationLoading,
  } = useQuery(
    ['organization', organizationId],
    () =>
      organizationApi.organizationControllerGetOrganizationById({
        id: organizationId!,
      }),
    {
      retry: 2,
      enabled: !!organizationId && isOrganizationAdminRoleSelected,
    },
  );

  // Organization working hours mutation (for organization admins)
  const {
    mutateAsync: mutateAsyncOrganizationWorkingHours,
    isLoading: isOrganizationWorkingHoursLoading,
  } = useMutation({
    mutationFn: ({
      workingHours,
      dryRun,
    }: {
      workingHours: AvailabilitySlotDto[];
      dryRun?: boolean;
    }) =>
      organizationApi.organizationControllerUpdateOrganizationWorkingHours({
        id: organizationId as string,
        updateWorkingHoursDto: {
          workingHours,
          dryRun,
        },
      }),
  });

  // Organization time off mutation (for organization admins)
  const {
    mutateAsync: mutateAsyncOrganizationTimeOff,
    isLoading: isOrganizationTimeOffLoading,
  } = useMutation({
    mutationFn: ({
      timeOff,
      dryRun,
    }: {
      timeOff: any[];
      dryRun?: boolean;
    }) =>
      organizationApi.organizationControllerUpdateOrganizationTimeOff({
        id: organizationId as string,
        updateTimeOffDto: {
          timeOff: timeOff as any,
          dryRun,
        },
      }),
  });

  // Dry-run preview → if conflicts, open the confirm modal and apply only on
  // Potwierdź; otherwise apply immediately. The returned promise resolves with
  // the apply response (success), or `{ cancelled: true }` if the user backs
  // out — callers skip their success handling in that case.
  const gatedSave = (
    apply: () => Promise<AxiosResponse<any>>,
    preview: () => Promise<AxiosResponse<any>>,
  ): Promise<any> =>
    new Promise((resolve, reject) => {
      preview()
        .then((pre) => {
          const conflicts = pre.data?.conflictingVisits ?? [];
          if (conflicts.length === 0) {
            apply().then(resolve, reject);
            return;
          }
          setConflictingVisits(conflicts);
          pendingChangeRef.current = { apply, resolve, reject };
          setConflictModalOpen(true);
        })
        .catch(reject);
    });

  const onSave = (workingHours: AvailabilitySlotDto[]): Promise<any> =>
    gatedSave(
      () => mutateAsyncOrganizationWorkingHours({ workingHours, dryRun: false }),
      () => mutateAsyncOrganizationWorkingHours({ workingHours, dryRun: true }),
    );

  const onSaveTimeOff = (timeOffDto: Object[]): Promise<any> =>
    gatedSave(
      () =>
        mutateAsyncOrganizationTimeOff({ timeOff: timeOffDto, dryRun: false }),
      () =>
        mutateAsyncOrganizationTimeOff({ timeOff: timeOffDto, dryRun: true }),
    );

  const handleConfirmChange = async () => {
    const pending = pendingChangeRef.current;
    if (!pending) return;
    setIsConfirming(true);
    try {
      pending.resolve(await pending.apply());
    } catch (error) {
      pending.reject(error);
    } finally {
      setIsConfirming(false);
      setConflictModalOpen(false);
      pendingChangeRef.current = null;
    }
  };

  const handleCancelChange = () => {
    pendingChangeRef.current?.resolve({ cancelled: true });
    pendingChangeRef.current = null;
    setConflictModalOpen(false);
  };

  const handleOpenTimeOffModal = () => {
    setIsTimeOffModalOpen(true);
  };

  const handleCloseTimeOffModal = () => {
    setIsTimeOffModalOpen(false);
  };

  if (!organizationId) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12} xl={12}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flex: 1,
            boxShadow: theme.customShadows.z24,
          }}
        >
          <Stack sx={{ height: 1, minHeight: '560px', flex: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              pb={2}
            >
              <Typography variant="h6">{translateKey("hardcoded.grafik_placowki")}</Typography>
              {isOrganizationLoading ? (
                <Skeleton variant="rounded" sx={{ width: 164, height: 36 }} />
              ) : (
                <Button
                  size="medium"
                  onClick={handleOpenTimeOffModal}
                  disabled={
                    isOrganizationWorkingHoursLoading ||
                    isOrganizationLoading ||
                    isOrganizationTimeOffLoading
                  }
                  variant="contained"
                  color="primary"
                  startIcon={
                    <Iconify
                      width="20px"
                      height="20px"
                      icon="mdi:calendar-remove"
                    />
                  }
                >
                  Ustaw dni wolne
                </Button>
              )}
            </Stack>
            {isOrganizationLoading ? (
              <CalendarScheduleSkeleton />
            ) : organizationData ? (
              <CalendarSchedule
                data={(organizationData?.data as any)?.workingHours || []}
                timeOff={organizationData?.data?.timeOff || []}
                refetch={refetchOrganization}
                onSave={onSave}
                isLoading={
                  isOrganizationLoading ||
                  isOrganizationWorkingHoursLoading ||
                  isOrganizationTimeOffLoading
                }
              />
            ) : null}
          </Stack>
          {organizationData && (
            <TimeOffModal
              isOpen={isTimeOffModalOpen}
              onClose={handleCloseTimeOffModal}
              timeOff={organizationData?.data?.timeOff || []}
              refetch={refetchOrganization}
              onSave={onSaveTimeOff}
              isOrganizationTimeOff
            />
          )}
          <ScheduleConflictWarningModal
            open={conflictModalOpen}
            onClose={handleCancelChange}
            onConfirm={handleConfirmChange}
            isConfirming={isConfirming}
            conflicts={conflictingVisits}
          />
        </Card>
      </Grid>
    </Grid>
  );
}
